import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { richTextListStyles } from "../../common/richTextListStyles"
import {
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_MOBILE,
} from "../../layout/Header/styles"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* ------------------------------------------------------------------ */
/* Root                                                                */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.spacing[40]}px;
  padding: ${sizes.spacing[48]}px 0;
  scroll-margin-top: ${HEADER_HEIGHT_MOBILE}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[48]}px;
    padding: ${sizes.spacing[64]}px 0;
    scroll-margin-top: ${HEADER_HEIGHT_DESKTOP}px;
  }

  .MuiTabs-root {
    width: 100%;
    max-width: 100%;
  }
`

/* ------------------------------------------------------------------ */
/* Header — title section with decorative chevron                      */
/* ------------------------------------------------------------------ */
export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;
`

export const Title = styled(Typography)`
  color: ${colors.light.text.primary};
`

export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const Subtitle = styled(Typography)`
  color: ${colors.light.text.primary};
  text-align: start;
`

/* ------------------------------------------------------------------ */
/* Card Grid                                                           */
/* ------------------------------------------------------------------ */
export const CardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: ${sizes.spacing[24]}px;
    row-gap: ${sizes.spacing[48]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Card — wrapper with flip behavior                                   */
/* ------------------------------------------------------------------ */
/* EGDE-519: the management card performs a real 3D Y-flip (per the approved
   flip-box reference), replacing the old opacity crossfade. The wrapper only
   provides perspective — overflow/border/radius live on the faces, because
   overflow:hidden on the 3D parent would flatten preserve-3d and a static
   wrapper border would not rotate with the card. */
export const CardWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: ${sizes.borderRadius[16]}px;
  cursor: pointer;
  perspective: 1200px;
`

/* Rotor — carries the flip; faces sit on its two sides */
export const CardRotor = styled.div<{ $flipped: boolean }>`
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
  transition: transform 0.6s ease;
  transform: rotateY(${(p) => (p.$flipped ? "180deg" : "0deg")});

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/* EGDE-610: mobile Safari ignores `backface-visibility: hidden` on a face that
   also has `overflow: hidden` + a border-radius, so the front kept painting
   through the flipped-open back — its gradient washed the white card dark and its
   name/role showed as mirror-image text (the front is at 180° once the rotor
   turns). Rather than fight that, each face also hides itself outright when it is
   the away-facing one. The zero-duration transition delayed to half the 0.6s
   rotation swaps the faces exactly edge-on, so the flip still reads as one solid
   card. `backface-visibility` stays for browsers that honour it. */
const faceSwap = `
  transition:
    opacity 0s linear 0.3s,
    visibility 0s linear 0.3s;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/* Card front — image + gradient overlay + name/role                  */
export const CardFront = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border-radius: ${sizes.borderRadius[16]}px;
  border: 1px solid ${colors.primitives.neutral300};
  overflow: hidden;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  pointer-events: ${(p) => (p.$visible ? "auto" : "none")};
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  visibility: ${(p) => (p.$visible ? "visible" : "hidden")};
  ${faceSwap}
`

export const CardImageContainer = styled.div`
  position: absolute;
  inset: 0;
`

/* EGDE-262: match the Figma fill — a full-card linear gradient from transparent
   black at the top to solid black at the bottom (#000000 0% → #000000 100%,
   spanning the whole card; the card itself is a square aspect-ratio 1/1, not the
   192px Figma frame). The earlier bottom-70% ramp left the name line in a
   near-transparent band, so white text stayed hard to read over bright photos.
   Full-height coverage keeps heads (upper region) light while the bottom darkens
   to near-opaque where the name/role sit. */
export const CardGradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  /* IMPORTANT — no explicit 0%/100% stops here. stylis-plugin-rtl flips gradient
     color-stop PERCENTAGES in RTL (0% becomes 100%), which collapsed this overlay
     to a no-op black hairline and left the white name/role unreadable over bright
     photos. THAT is the bug QA hit on both prior attempts. Omitting the
     percentages (they default to 0% top / 100% bottom) leaves nothing for the RTL
     plugin to flip, so the transparent-top to solid-black-bottom ramp renders
     identically in LTR and RTL. An @ noflip comment can't be relied on — the
     production CSS build strips it before stylis runs. */
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1));
  z-index: 1;
  pointer-events: none;
`

export const CardFrontInfo = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  padding: ${sizes.spacing[24]}px;
`

export const CardFrontText = styled(Typography)`
  color: ${colors.primitives.neutral0};
  text-align: start;
`

export const CardFrontName = styled(Typography)`
  color: ${colors.primitives.neutral0};
  text-align: start;
  font-weight: 700 !important;
`

/* Card back — white card with thumbnail, name, role, description.
   EGDE-519: pre-rotated 180° so it reads correctly once the rotor flips —
   without this the back face shows as mirror-image text. */
export const CardBack = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  background: ${colors.primitives.neutral0};
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
  padding: ${sizes.spacing[24]}px;
  border-radius: ${sizes.borderRadius[16]}px;
  border: 1px solid ${colors.primitives.neutral300};
  transform: rotateY(180deg);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  pointer-events: ${(p) => (p.$visible ? "auto" : "none")};
  overflow-y: auto;
  /* EGDE-610: hidden while it is the away-facing side — see faceSwap above. This
     also keeps the back's CV/career links out of the tab order while the card is
     closed, which pointer-events alone did not do. */
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  visibility: ${(p) => (p.$visible ? "visible" : "hidden")};
  ${faceSwap}
`

export const CardBackProfile = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 13px;
`

export const CardBackThumbnail = styled.div`
  position: relative;
  width: 86px;
  height: 86px;
  border-radius: ${sizes.borderRadius[16]}px;
  overflow: hidden;
  flex-shrink: 0;
`

export const CardBackTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[6]}px;
  min-width: 0;
`

export const CardBackName = styled(Typography)`
  color: ${colors.primitives.neutral600};
  text-align: start;
  font-weight: 700 !important;
  font-size: 20px !important;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px !important;
  }
`

export const CardBackRole = styled(Typography)`
  color: ${colors.primitives.neutral600};
  text-align: start;
  font-size: 18px !important;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 18px !important;
  }
`

export const CardBackDescription = styled.div`
  color: ${colors.primitives.neutral600};
  text-align: start;
  font-size: 18px;
  line-height: 26px;

  & p {
    margin: 0;
  }

  & a {
    /* EGDE-327: rich-text body hyperlinks render blue, not orange/green */
    /* !important overrides inline color:#... pasted into CMS rich text */
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }

  /* EGDE-546: the global reset strips list padding, so the outside-positioned
     default markers were clipped by the card's rounded overflow — lists read
     as plain text. The shared styles draw the chevron/number markers inside. */
  ${richTextListStyles}
`

export const CardBackLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  margin-top: auto;
`

export const CardBackLink = styled(Link)`
  /* EGDE-391: person-card CV/career links render blue, not orange */
  color: ${colors.primitives.linkBlue};
  text-decoration: underline;
  font-size: 16px;
  line-height: 22px;
`

export const CardBackExternalLink = styled.a`
  /* EGDE-391: person-card CV/career links render blue, not orange */
  color: ${colors.primitives.linkBlue};
  text-decoration: underline;
  font-size: 16px;
  line-height: 22px;
`

/* ------------------------------------------------------------------ */
/* Empty state                                                         */
/* ------------------------------------------------------------------ */
export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 200px;
`

/* ------------------------------------------------------------------ */
/* Pagination                                                          */
/* ------------------------------------------------------------------ */
export const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[16]}px;
`

export const PaginationArrow = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${sizes.borderRadius.full}px;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: ${colors.primitives.neutral600};
  transition:
    opacity 0.2s ease,
    background-color 0.2s ease;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  /* EGDE-496: the glyph is already black at rest, so hover paints the
     neutral200 disc behind it instead (neutral100 is indistinguishable from
     white). */
  &:hover:not(:disabled) {
    background-color: ${colors.primitives.neutral200};
  }
`

export const PaginationDot = styled.button<{ $active: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid
    ${(p) =>
      p.$active ? colors.primitives.warning500 : colors.primitives.neutral300};
  background: ${(p) =>
    p.$active ? colors.primitives.secondary500 : colors.primitives.neutral0};
  color: ${colors.primitives.neutral600};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;

  /* EGDE-496: same subtle ring-darkening hover as the lobby page circles. */
  &:hover:not(:disabled) {
    border-color: ${colors.primitives.neutral500};
  }
`

export const PaginationDotOnly = styled.button<{ $active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: ${sizes.borderRadius.full}px;
  border: none;
  background: ${(p) =>
    p.$active ? colors.primitives.secondary500 : colors.primitives.neutral300};
  cursor: pointer;
  padding: 0;
  transition: background 0.2s ease;

  /* EGDE-496: inactive dots darken slightly under the cursor. */
  &:hover {
    background: ${(p) =>
      p.$active
        ? colors.primitives.secondary500
        : colors.primitives.neutral400};
  }
`
